/**
 * Hook for fetching and managing positions data with pagination support.
 * Uses SWR for caching, deduplication, and automatic revalidation.
 */

import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { fetchPositions } from '@/services/api';
import type { Position } from '@/types';

/** Default page size for positions */
export const DEFAULT_PAGE_SIZE = 25;

interface UsePositionsOptions {
  /** Refresh interval in milliseconds (default: 60000) */
  refreshInterval?: number;
  /** Whether to revalidate on window focus (default: true) */
  revalidateOnFocus?: boolean;
  /** Page size (default: 25) */
  pageSize?: number;
  /** Filter by position status */
  status?: 'ACTIVE' | 'CLOSED';
}

/**
 * Fetch positions for a specific account with pagination.
 *
 * @param accountId - The account ID to fetch positions for
 * @param options - Configuration options
 * @returns Positions data, loading state, error, pagination controls, and refresh function
 */
export function usePositions(
  accountId: string | null,
  options: UsePositionsOptions = {}
) {
  const {
    refreshInterval = 60000, // 60 second default refresh
    revalidateOnFocus = true,
    pageSize = DEFAULT_PAGE_SIZE,
    status,
  } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);

  // The cursor for the current page
  const currentCursor = cursorHistory[currentPage - 1] ?? null;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    accountId ? ['positions', accountId, pageSize, currentCursor, status] : null,
    async () => {
      if (!accountId) return { positions: [], count: 0, last_evaluated_key: undefined };
      return fetchPositions(accountId, {
        limit: pageSize,
        lastEvaluatedKey: currentCursor,
        status,
      });
    },
    {
      refreshInterval,
      revalidateOnFocus,
      dedupingInterval: 5000,
    }
  );

  // Store the next cursor when data arrives
  const nextCursor = data?.last_evaluated_key ?? null;

  const goToNextPage = useCallback(() => {
    if (!nextCursor) return;
    setCurrentPage((prev) => {
      const next = prev + 1;
      setCursorHistory((history) => {
        // Only add cursor if we don't already have it
        if (history.length <= prev) {
          return [...history, nextCursor];
        }
        // Update existing cursor in case data changed
        const updated = [...history];
        updated[prev] = nextCursor;
        return updated;
      });
      return next;
    });
  }, [nextCursor]);

  const goToPrevPage = useCallback(() => {
    if (currentPage <= 1) return;
    setCurrentPage((prev) => prev - 1);
  }, [currentPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Reset pagination when account changes
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setCursorHistory([null]);
  }, []);

  return {
    positions: data?.positions ?? [],
    count: data?.count ?? 0,
    error: error?.message ?? null,
    isLoading,
    isRefreshing: isValidating && !isLoading,
    refresh: () => mutate(),
    // Pagination
    currentPage,
    hasNextPage: !!nextCursor,
    hasPrevPage: currentPage > 1,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    resetPagination,
    pageSize,
  };
}

/**
 * Filter positions by status.
 */
export function filterPositionsByStatus(
  positions: Position[],
  status: 'ACTIVE' | 'CLOSED' | 'all'
): Position[] {
  if (status === 'all') return positions;
  return positions.filter((p) => p.position_status === status);
}

/**
 * Filter positions by symbol search.
 */
export function filterPositionsBySymbol(
  positions: Position[],
  search: string
): Position[] {
  if (!search) return positions;
  const lowerSearch = search.toLowerCase();
  return positions.filter((p) => p.symbol.toLowerCase().includes(lowerSearch));
}

/**
 * Maximum number of positions to fetch across all pages.
 * Prevents unbounded memory usage for very active accounts.
 */
const MAX_POSITIONS_LIMIT = 1000;

/**
 * Fetch ALL positions for an account (auto-paginates through all pages).
 * Caps at MAX_POSITIONS_LIMIT to prevent unbounded fetching.
 * Use this when you need the complete dataset (e.g., dashboard metrics, risk, allocation).
 */
async function fetchAllPositionsForAccount(
  accountId: string,
  status?: 'ACTIVE' | 'CLOSED'
): Promise<{ positions: Position[]; count: number; truncated: boolean }> {
  const allPositions: Position[] = [];
  let cursor: string | null = null;

  do {
    const response = await fetchPositions(accountId, {
      limit: 100,
      lastEvaluatedKey: cursor,
      status,
    });
    allPositions.push(...response.positions);
    cursor = response.last_evaluated_key ?? null;

    if (allPositions.length >= MAX_POSITIONS_LIMIT) {
      return {
        positions: allPositions.slice(0, MAX_POSITIONS_LIMIT),
        count: MAX_POSITIONS_LIMIT,
        truncated: true,
      };
    }
  } while (cursor);

  return { positions: allPositions, count: allPositions.length, truncated: false };
}

interface UseAllPositionsOptions {
  /** Refresh interval in milliseconds (default: 60000) */
  refreshInterval?: number;
  /** Whether to revalidate on window focus (default: true) */
  revalidateOnFocus?: boolean;
  /** Filter by position status */
  status?: 'ACTIVE' | 'CLOSED';
}

/**
 * Fetch all positions for an account without pagination.
 * Auto-paginates through all pages server-side.
 * Use for dashboard views that need the complete dataset.
 */
export function useAllPositions(
  accountId: string | null,
  options: UseAllPositionsOptions = {}
) {
  const {
    refreshInterval = 60000,
    revalidateOnFocus = true,
    status,
  } = options;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    accountId ? ['all-positions', accountId, status] : null,
    async () => {
      if (!accountId) return { positions: [], count: 0, truncated: false };
      return fetchAllPositionsForAccount(accountId, status);
    },
    {
      refreshInterval,
      revalidateOnFocus,
      dedupingInterval: 5000,
    }
  );

  return {
    positions: data?.positions ?? [],
    count: data?.count ?? 0,
    truncated: data?.truncated ?? false,
    error: error?.message ?? null,
    isLoading,
    isRefreshing: isValidating && !isLoading,
    refresh: () => mutate(),
  };
}
